import React, { useCallback, useEffect, useState } from 'react';
import { healthsherpa } from './api';
import { MetalBadge } from './hsBadges';
import { LogoTile } from './HealthSherpaPresent';
import { PolicyBadge, fmtDay, usdCents } from './HealthSherpaPolicy';
import { policyColors, rowTone } from './hsColors';
import { displayAddress, displayName } from './hsLabels';
import { fs } from './hsScale';   // 5.31.0: font sizes scale with the page's Text size
import { transfer } from './hsLock';   // 5.44.0 every way into HealthSherpa takes the "in use" lock
import PersonalInfo from './HsPersonalInfo';                            // 5.46.0

// =============================================================================
// PersonView (5.20.0) — ONE component for a HealthSherpa person / household, used by:
//   - the HealthSherpa page's Clients tab (the person page; Back returns to the list), and
//   - the InsureDocs client view's "🩺 Health coverage" side panel (compact).
// Header (name, household members, the InsureDocs client or Assign, address / phone / email from the newest
// export, Open in HealthSherpa, Refresh now, alerts), the TIMELINE by plan year (newest first, a highlighted
// line when the carrier changed), and the STATUS HISTORY (changes; "Show every check" adds every check).
// Every value says where it came from and when (CT). Names / contact data are shown, never logged.
// =============================================================================

const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
// 5.20.4: names are shown in normal case — "Last, First", each part through hsLabels.displayName (only an all-caps /
// all-lower name changes; mixed case stays as typed). Display only: the stored names never change.
export const personName = (p) => {
  if (!p) return '—';
  if (!p.last_name && !p.first_name) return p.display_name ? displayName(p.display_name) : '—';
  return [displayName(p.last_name), displayName(p.first_name)].filter(Boolean).join(', ');
};
const memberName = (m) => [m.first, m.last].filter(Boolean).map(displayName).join(' ');
const REL = { primary: 'Primary', spouse: 'Spouse', dependent: 'Dependent' };
const LABEL_TONE = { disagree: 'attention', stale: 'neutral', no_api_aor: 'neutral', other_aor: 'neutral' };

export function usePersonColors(darkMode) {
  return {
    darkMode,
    bg: darkMode ? '#1a1a1a' : '#f5f5f5',
    panel: rowTone('panel', darkMode),
    border: darkMode ? '#3a3f46' : '#d5d9df',
    text: rowTone('text', darkMode),
    sub: rowTone('subtext', darkMode),
    link: darkMode ? '#7fb2e5' : '#1f5d99',
  };
}

function Chip({ tone, darkMode, children, title }) {
  const t = policyColors(tone || 'neutral', darkMode);
  return (
    <span title={title} style={{ display: 'inline-block', background: t.bg, color: t.text, border: `1px solid ${t.border}`, borderRadius: 10,
      padding: '1px 9px', fontSize: fs(12), fontWeight: 600, whiteSpace: 'nowrap' }}>{children}</span>
  );
}

function KV({ k, children, c, width = 130 }) {
  return (
    <div style={{ display: 'flex', gap: 10, fontSize: fs(13), padding: '2px 0' }}>
      <div style={{ width, color: c.sub, flex: 'none' }}>{k}</div>
      <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{children}</div>
    </div>
  );
}

// --- Refresh now: once per person per day (5.20.2) ---------------------------------------------------------
// One button for the person page, the InsureDocs Health coverage panel and the Clients list drop-down. The server
// enforces the rule; `manual` (the payload's manual_refresh_today) only decides how the button looks:
//   none today                -> "Refresh now"
//   done today, not an admin  -> disabled "Refreshed today at 8:30 AM by Henry — available again tomorrow"
//   done today, admin         -> the same note + "Refresh again (admin)" behind a confirm (override)
// 5.20.3: only a refresh HealthSherpa ANSWERED counts; after an unanswered press the button stays "Refresh now".
const ctTime = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' }) : '?';
};
export const NOT_COUNTED = "HealthSherpa didn't answer — this try wasn't counted. You can try again.";
export function RefreshNowButton({ personId, manual, isAdmin, onDone, onMessage, className, style, disabledStyle, noteStyle, title }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(manual || null);
  useEffect(() => { setState(manual || null); }, [manual]);
  const who = state ? (state.by_first_name || 'the system') : '';
  const note = state ? `Refreshed today at ${state.at_label || ctTime(state.at)} by ${who} — available again tomorrow` : null;
  const press = async () => {
    let override = false;
    if (state) {
      if (!isAdmin) return;
      if (!window.confirm(`Already refreshed today at ${state.at_label || ctTime(state.at)} by ${who} — refresh again anyway?`)) return;
      override = true;
    }
    setBusy(true);
    if (onMessage) onMessage(null);
    try {
      const r = await healthsherpa.policy.refreshPerson(personId, override);
      if (onDone) onDone(r.data);
      // 5.20.3: a press HealthSherpa didn't answer doesn't use up the day — the payload's manual_refresh_today stays
      // empty, so the button stays "Refresh now"; say why
      const k = (r.data && r.data.run && r.data.run.counts) || {};
      if (!(Number(k.checked) >= 1) && onMessage) onMessage(NOT_COUNTED);
    } catch (e) {
      const d = e?.response?.data;
      if (e?.response?.status === 429 && d && d.refreshed_at) {
        setState({ at: d.refreshed_at, at_label: ctTime(d.refreshed_at), by_first_name: d.refreshed_by });
      }
      if (onMessage) onMessage(typeof d?.detail === 'string' ? d.detail : (e?.message || 'Refresh failed'));
    }
    setBusy(false);
  };
  if (state && !isAdmin) {
    return <button className={className} style={{ ...style, ...disabledStyle }} disabled data-testid="hs-refresh-now">{note}</button>;
  }
  return (
    <>
      {note && <span style={noteStyle}>{note}</span>}
      <button className={className} style={style} disabled={busy} onClick={press} title={title} data-testid="hs-refresh-now">
        {busy ? 'Refreshing…' : state ? 'Refresh again (admin)' : 'Refresh now'}
      </button>
    </>
  );
}

const smallBtn = (c, primary, disabled) => ({
  padding: '6px 12px', fontSize: fs(13), borderRadius: 5, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
  border: primary ? 'none' : `1px solid ${c.border}`, background: primary ? '#185FA5' : c.panel, color: primary ? '#fff' : c.text,
  fontWeight: primary ? 600 : 400, whiteSpace: 'nowrap',
});

// --- one application in the timeline ------------------------------------------------------------------

function TimelineEntry({ a, c, compact }) {
  const pols = (a.api && a.api.policies) || [];
  const pn = a.policy_numbers || {};
  return (
    <div style={{ border: `1px solid ${c.border}`, borderRadius: 8, padding: 12, background: c.panel, marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        {a.logo_url ? <LogoTile url={a.logo_url} name={a.carrier} height={30} maxWidth={120} darkMode={c.darkMode} />
          : <strong style={{ fontSize: fs(14) }}>{a.carrier || 'Carrier not in an export yet'}</strong>}
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{ fontWeight: 600, fontSize: fs(14) }}>{a.plan_name || 'Plan not in an export yet'}</div>
          <div style={{ fontSize: fs(12), color: c.sub }}>
            {a.carrier && a.logo_url ? `${a.carrier} · ` : ''}{fmtDay(a.effective_date)} → {a.end_date ? fmtDay(a.end_date) : '—'}
          </div>
        </div>
        {a.metal_level && <MetalBadge code={String(a.metal_level).toLowerCase().replace(/\s+/g, '_')} label={a.metal_level} darkMode={c.darkMode} />}
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <PolicyBadge value={a.status} darkMode={c.darkMode} />
          {a.payment && <PolicyBadge value={a.payment === 'unpaid_first' ? 'unpaid_binder' : a.payment} darkMode={c.darkMode} />}
        </span>
      </div>
      {a.labels && a.labels.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {a.labels.map((l) => <Chip key={l.kind} tone={LABEL_TONE[l.kind]} darkMode={c.darkMode}>{l.text}</Chip>)}
          {a.docs_needed && <Chip tone="attention" darkMode={c.darkMode}>Documents needed</Chip>}
        </div>
      )}
      {(!a.labels || a.labels.length === 0) && a.docs_needed && (
        <div style={{ marginTop: 8 }}><Chip tone="attention" darkMode={c.darkMode}>Documents needed</Chip></div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: compact ? '1fr' : 'repeat(auto-fit, minmax(320px, 1fr))', columnGap: 24, marginTop: 8 }}>
        <div>
          <KV k="Net premium" c={c}>{a.net_premium_cents === null || a.net_premium_cents === undefined ? '—' : `${usdCents(a.net_premium_cents)} / month`}</KV>
          <KV k="Agent of record" c={c}>{a.agent_of_record ? (a.aor_ours ? `Ours — ${a.agent_of_record}` : a.agent_of_record) : '—'}</KV>
          <KV k="Members covered" c={c}>{(a.members_covered || []).length ? a.members_covered.map((n) => String(n || '').split(/\s+/).map(displayName).join(' ')).join(', ') : '—'}</KV>
        </div>
        <div>
          <KV k="Exchange policy" c={c}>{(pn.exchange || []).length ? pn.exchange.join(', ') : '—'}</KV>
          <KV k="Carrier policy" c={c}>{pn.issuer || '—'}</KV>
          <KV k="Subscriber ID" c={c}>{pn.ffm_subscriber || '—'}</KV>
        </div>
      </div>
      {pols.map((p) => (
        <div key={p.exchange_policy_id || 'none'} style={{ marginTop: 6, fontSize: fs(13), color: c.text }}>
          {pols.length > 1 && <span style={{ color: c.sub }}>Policy {p.exchange_policy_id || '—'}: </span>}
          paid through {fmtDay(p.paid_through_date)} · current balance {usdCents(p.current_balance_cents)} · past due {usdCents(p.past_due_balance_cents)}
          {p.grace_period_start_date ? ` · grace-period start date ${fmtDay(p.grace_period_start_date)}` : ''}
        </div>
      ))}
      <div style={{ marginTop: 8, fontSize: fs(12), color: c.sub, lineHeight: 1.6 }}>
        <div>HealthSherpa API — {a.api && a.api.checked_label ? `last refreshed ${a.api.checked_label}` : 'not refreshed yet'}
          {a.api && a.api.hs_changed_label ? ` (HealthSherpa last changed it ${a.api.hs_changed_label})` : ''}
          {a.api && pols.length === 0 && a.api.result ? ` — ${a.api.result === 'no_policies' ? 'no policies returned' : a.api.result === 'not_found' ? 'not found' : a.api.result === 'error' ? 'the last check failed' : a.api.result}` : ''}</div>
        {a.export && <div>Export of {a.export.generated_label}: {a.export.policy_status || '—'}{a.export.paid_through_date ? `, paid through ${fmtDay(a.export.paid_through_date)}` : ''}</div>}
      </div>
    </div>
  );
}

// --- status history ---------------------------------------------------------------------------------------

export function History({ personId, c, reloadKey }) {     // 5.22.0: also the Clients panel
  const [every, setEvery] = useState(false);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { setPage(1); }, [every, personId]);
  useEffect(() => {
    let alive = true;
    healthsherpa.policy.personHistory(personId, { every_check: every ? 1 : 0, page, per_page: 50 })
      .then((r) => { if (alive) { setData(r.data); setError(null); } })
      .catch((e) => { if (alive) setError(detailOf(e)); });
    return () => { alive = false; };
  }, [personId, every, page, reloadKey]);
  return (
    <div style={{ marginTop: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontSize: fs(15), fontWeight: 700 }}>Status history</div>
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: fs(13), cursor: 'pointer' }}>
          <input type="checkbox" checked={every} onChange={(e) => setEvery(e.target.checked)} data-testid="hs-every-check" />
          Show every check
        </label>
        {data && <span style={{ fontSize: fs(12), color: c.sub }}>{data.total} line{data.total === 1 ? '' : 's'}</span>}
      </div>
      {error && <div style={{ color: '#c0392b', fontSize: fs(13), marginTop: 6 }}>{error}</div>}
      {data && data.lines.length === 0 && <div style={{ fontSize: fs(13), color: c.sub, marginTop: 6 }}>No history yet.</div>}
      {data && data.lines.length > 0 && (
        <div style={{ marginTop: 6, border: `1px solid ${c.border}`, borderRadius: 8, background: c.panel }}>
          {data.lines.map((h, i) => (
            <div key={`${h.at}-${i}`} style={{ display: 'flex', gap: 10, padding: '6px 10px', fontSize: fs(13), borderTop: i ? `1px solid ${c.border}` : 'none',
              color: h.kind === 'check' || h.kind === 'export' ? c.sub : c.text }}>
              <span style={{ flex: 'none', minWidth: 40, color: c.sub }}>{h.plan_year}</span>
              <span style={{ flex: 1 }}>{h.label}</span>
              {h.source_label && <span style={{ flex: 'none', fontSize: fs(11), color: c.sub }}>{h.source_label}</span>}
            </div>
          ))}
        </div>
      )}
      {data && data.pages > 1 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontSize: fs(13) }}>
          <button style={smallBtn(c, false, page <= 1)} disabled={page <= 1} onClick={() => setPage(page - 1)}>Newer</button>
          <span style={{ color: c.sub }}>page {data.page} of {data.pages}</span>
          <button style={smallBtn(c, false, page >= data.pages)} disabled={page >= data.pages} onClick={() => setPage(page + 1)}>Older</button>
        </div>
      )}
    </div>
  );
}

// --- the person ------------------------------------------------------------------------------------------

export default function PersonView({ personId, darkMode, compact, onBack, onOpenClient, onAssign, onChanged, reloadKey = 0 }) {
  const c = usePersonColors(darkMode);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [histKey, setHistKey] = useState(0);
  const [showPast, setShowPast] = useState(!compact);
  const load = useCallback(() => {
    healthsherpa.policy.person(personId).then((r) => { setData(r.data); setError(null); })
      .catch((e) => setError(e?.response?.status === 404 ? 'This person is not available.' : detailOf(e)));
  }, [personId]);
  useEffect(() => { setData(null); setMsg(null); load(); }, [load, reloadKey]);

  const refreshed = (d) => {          // 5.20.2: the Refresh now answer carries the whole person payload
    setData(d.person);
    setHistKey((k) => k + 1);
    const k = d.run.counts || {};
    setMsg(`Refreshed ${d.run.started_label}: ${k.checked || 0} checked${k.changes ? `, ${k.changes} changed` : ''}${k.errors ? `, ${k.errors} failed` : ''}.`);
  };
  const unassign = async () => {
    const cl = data.person.client;
    if (!window.confirm(`Unassign this person from ${cl.number ? `${cl.number} ` : ''}${displayName(cl.name)}? All of their HealthSherpa applications are unlinked.`)) return;
    try {
      const r = await healthsherpa.policy.unassignPerson(personId);
      if (onChanged) onChanged(r.data.person_id);
      if (r.data.person_id === personId) load();
    } catch (e) { setMsg(detailOf(e)); }
  };

  if (error) return <div style={{ padding: 18, color: c.text }}>{onBack && <button style={smallBtn(c)} onClick={onBack}>← Back</button>}<div style={{ marginTop: 10, fontSize: fs(13) }}>{error}</div></div>;
  if (!data) return <div style={{ padding: 18, fontSize: fs(13), color: c.sub }}>Loading…</div>;
  const p = data.person;
  const years = data.years || [];
  const current = years.filter((y) => y.current);
  const past = years.filter((y) => !y.current);
  const yearBlock = (y) => (
    <div key={y.plan_year} style={{ marginTop: 14 }} data-testid="hs-timeline-year">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <div style={{ fontSize: fs(16), fontWeight: 700 }}>{y.plan_year}</div>
        {y.current && <span style={{ fontSize: fs(12), color: c.sub }}>current plan year</span>}
        {y.applications.length > 1 && <span style={{ fontSize: fs(12), color: c.sub }}>{y.applications.length} applications</span>}
      </div>
      {y.switch && (
        <div style={{ marginTop: 6, padding: '6px 10px', borderRadius: 6, fontSize: fs(13), fontWeight: 600,
          background: policyColors('attention', darkMode).bg, color: policyColors('attention', darkMode).text, border: `1px solid ${policyColors('attention', darkMode).border}` }}>
          ⇄ Switched from {y.switch.from} to {y.switch.to} for {y.switch.year}
        </div>
      )}
      {y.applications.map((a) => <TimelineEntry key={a.application_id} a={a} c={c} compact={compact} />)}
    </div>
  );
  const members = p.members || [];
  return (
    <div style={{ padding: compact ? 14 : 18, color: c.text, maxWidth: compact ? undefined : 1400 }} data-testid="hs-person">
      {onBack && <button style={{ ...smallBtn(c), marginBottom: 12 }} onClick={onBack} data-testid="hs-person-back">← Back to the list</button>}
      <div style={{ border: `1px solid ${c.border}`, borderRadius: 8, background: c.panel, padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: fs(compact ? 17 : 21), fontWeight: 700 }} data-testid="hs-person-name">{personName(p)}</div>
            <div style={{ fontSize: fs(13), color: c.sub, marginTop: 2 }}>
              {[displayAddress(p.street || p.address), [displayAddress(p.city), p.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ') || 'No address yet'}
              {p.phone ? ` · ${p.phone}` : ''}{p.email ? ` · ${p.email}` : ''}
            </div>
            {p.export_label && <div style={{ fontSize: fs(11), color: c.sub, marginTop: 2 }}>Contact details from the export of {p.export_label}</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {p.hs_link && <button type="button" onClick={() => transfer(p.hs_link)} data-testid="hs-open-person"
              style={{ fontSize: fs(13), color: c.link, background: 'none', border: 'none', padding: 0, cursor: 'pointer', textDecoration: 'underline' }}>Open in HealthSherpa ↗</button>}
            <RefreshNowButton personId={personId} manual={data.manual_refresh_today} isAdmin={data.is_admin} onDone={refreshed}
              onMessage={setMsg} style={smallBtn(c, true, false)} disabledStyle={{ ...smallBtn(c, false, false), cursor: 'default', opacity: 0.85, whiteSpace: 'normal', textAlign: 'left' }}
              noteStyle={{ fontSize: fs(12), color: c.sub, maxWidth: 260 }}
              title={`Ask HealthSherpa now (at most ${data.refresh_max} applications, newest first) — once per client per day`} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', marginTop: 10, fontSize: fs(13) }}>
          <div>
            <span style={{ color: c.sub }}>InsureDocs client: </span>
            {p.client ? (
              <>
                {onOpenClient
                  ? <button onClick={() => onOpenClient(p.client.id)} style={{ background: 'none', border: 'none', padding: 0, color: c.link, cursor: 'pointer', fontSize: fs(13), fontWeight: 600 }}
                    title="Open in the InsureDocs tab">{p.client.number ? `${p.client.number} · ` : ''}{displayName(p.client.name)}{p.client.restricted ? ' 🔒' : ''}</button>
                  : <strong>{p.client.number ? `${p.client.number} · ` : ''}{displayName(p.client.name)}</strong>}
                {data.is_admin && <button style={{ ...smallBtn(c), marginLeft: 10, padding: '2px 8px', fontSize: fs(12) }} onClick={unassign}>Unassign</button>}
              </>
            ) : (data.is_admin && onAssign
              ? <button style={{ ...smallBtn(c, true), padding: '3px 10px' }} onClick={() => onAssign(p)} data-testid="hs-person-assign">Assign</button>
              : <span style={{ color: c.sub }}>Not assigned</span>)}
          </div>
        </div>
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: fs(12), color: c.sub, marginBottom: 4 }}>Household (latest export)</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {members.length === 0 && <span style={{ fontSize: fs(13), color: c.sub }}>—</span>}
            {members.map((m, i) => (
              <span key={`${m.relationship}-${i}`} style={{ fontSize: fs(13), border: `1px solid ${c.border}`, borderRadius: 6, padding: '2px 8px' }}>
                {memberName(m)}
                <span style={{ color: c.sub }}> · {REL[m.relationship] || m.relationship}{m.applying === true ? ', applying' : m.applying === false ? ', not applying' : ''}</span>
              </span>
            ))}
          </div>
        </div>
        {/* 5.46.0: personal info for quoting — masked until Show (logged) */}
        <div style={{ marginTop: 10 }} data-testid="hs-person-pi-box">
          <div style={{ fontSize: fs(12), color: c.sub, marginBottom: 4 }}>Personal info for quoting</div>
          <PersonalInfo personId={personId} darkMode={darkMode} testid="hs-person-pi" />
        </div>
        {data.alerts.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
            {data.alerts.map((al, i) => (
              <Chip key={`${al.key}-${al.plan_year}-${i}`} tone={al.key === 'other_aor' ? 'neutral' : 'attention'} darkMode={darkMode}>
                {al.label}{al.amount_cents ? ` ${usdCents(al.amount_cents)}` : ''}{al.since ? ` · since ${al.since}` : ''} ({al.plan_year})
              </Chip>
            ))}
          </div>
        )}
        {msg && <div style={{ fontSize: fs(13), marginTop: 8 }}>{msg}</div>}
      </div>

      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: fs(15), fontWeight: 700 }}>Coverage by plan year</div>
        {years.length === 0 && <div style={{ fontSize: fs(13), color: c.sub, marginTop: 6 }}>No applications.</div>}
        {current.map(yearBlock)}
        {past.length > 0 && (compact
          ? (
            <div style={{ marginTop: 10 }}>
              <button onClick={() => setShowPast(!showPast)} style={{ fontSize: fs(13), background: 'none', border: 'none', color: c.sub, cursor: 'pointer', padding: '4px 0' }}>
                {showPast ? '▾' : '▸'} Past plan years ({past.length})
              </button>
              {showPast && past.map(yearBlock)}
            </div>
          )
          : past.map(yearBlock))}
      </div>
      <History personId={personId} c={c} reloadKey={histKey} />
      <div style={{ fontSize: fs(12), color: c.sub, marginTop: 12 }}>Refreshed from HealthSherpa every day at {data.schedule}. Times are Central.</div>
    </div>
  );
}
